/**
 * AB#1901 — Boundary-parse helpers for BE enums.
 *
 * The BE serializes enums as STRING names via the global
 * `JsonStringEnumConverter` (see `Devanza.API/Program.cs:76-89`). These
 * helpers convert the wire form back to the integer enum values declared
 * in `src/types/enums.ts` and `src/types/api-responses.ts`, so consumer
 * code can keep comparing against `Enum.Member` integers without caring
 * which shape the wire happened to use.
 *
 * Each parser accepts:
 *   - the wire string ("Pending", "Male", "Percentage", …)  → integer
 *   - the legacy integer form (1, 2, …)                     → integer (passthrough)
 *   - null / undefined / unknown                            → null
 *
 * The int-form passthrough is intentional: if any seed, cached fixture,
 * or legacy path still emits ints, the parser still works.
 *
 * SEE: `devanza-claude/docs/features/AB1894/arch-brief.md` §6.2.
 */

import {
    Gender,
    ServiceType,
    AppointmentStatus,
    PaymentStatus,
    ProfileType,
} from '../types/enums';
import {
    DiscountType,
    CouponCodeType,
    CategoryApprovalStatus,
    AppointmentStatusKey,
} from '../types/api-responses';

// ─────────────────────────────────────────────────────────────────────────────
// Gender
// ─────────────────────────────────────────────────────────────────────────────
export function parseGender(raw: unknown): Gender | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Male': return Gender.Male;
            case 'Female': return Gender.Female;
            case 'Other': return Gender.Other;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw === Gender.Male || raw === Gender.Female || raw === Gender.Other) {
            return raw;
        }
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// ServiceType
// ─────────────────────────────────────────────────────────────────────────────
export function parseServiceType(raw: unknown): ServiceType | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'MenOnly': return ServiceType.MenOnly;
            case 'WomenOnly': return ServiceType.WomenOnly;
            case 'Everyone': return ServiceType.Everyone;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw === ServiceType.MenOnly || raw === ServiceType.WomenOnly || raw === ServiceType.Everyone) {
            return raw;
        }
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// AppointmentStatus
//
// NB: the FE enum here uses the legacy label `Confirmed=2`; the BE wire
// name for that ordinal is `Approved`. They are semantically the same
// lifecycle slot, so both strings ("Approved" or "Confirmed") map to
// AppointmentStatus.Confirmed=2. Arrived=6 and Started=7 were added in
// AB#1750 (post C4) and are not in the FE enum — they're returned as
// raw ints to match the existing AppointmentCard `statusToKey` mapping.
// ─────────────────────────────────────────────────────────────────────────────
export function parseAppointmentStatus(raw: unknown): number | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Pending': return AppointmentStatus.Pending;          // 1
            case 'Approved': return AppointmentStatus.Confirmed;       // 2 — BE Approved == FE Confirmed
            case 'Confirmed': return AppointmentStatus.Confirmed;      // 2 — back-compat alias
            case 'Completed': return AppointmentStatus.Completed;      // 3
            case 'Cancelled': return AppointmentStatus.Cancelled;      // 4
            case 'NoShow': return AppointmentStatus.NoShow;            // 5
            case 'Arrived': return 6;                                  // post-C4 extension
            case 'Started': return 7;                                  // post-C4 extension
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        // Accept any 1..7 — covers the post-C4 extension (6=Arrived, 7=Started)
        // not present in the FE enum declaration.
        if (raw >= 1 && raw <= 7) return raw;
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// PaymentStatus
// ─────────────────────────────────────────────────────────────────────────────
export function parsePaymentStatus(raw: unknown): number | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Paid': return PaymentStatus.Paid;            // 1
            case 'Unpaid': return PaymentStatus.Unpaid;        // 2
            case 'Upcoming': return PaymentStatus.Upcoming;    // 3
            case 'Draft': return PaymentStatus.Draft;          // 4
            case 'Late': return PaymentStatus.Late;            // 5
            case 'Refunded': return 6;                         // post-add extension
            case 'PartiallyRefunded': return 7;                // post-add extension
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw >= 1 && raw <= 7) return raw;
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// DiscountType
// ─────────────────────────────────────────────────────────────────────────────
export function parseDiscountType(raw: unknown): DiscountType | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Percentage': return DiscountType.Percentage;
            case 'FixedAmount': return DiscountType.FixedAmount;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw === DiscountType.Percentage || raw === DiscountType.FixedAmount) {
            return raw;
        }
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CouponCodeType
// ─────────────────────────────────────────────────────────────────────────────
export function parseCouponCodeType(raw: unknown): CouponCodeType | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Alphanumeric': return CouponCodeType.Alphanumeric;
            case 'Numeric': return CouponCodeType.Numeric;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw === CouponCodeType.Alphanumeric || raw === CouponCodeType.Numeric) {
            return raw;
        }
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// ProfileType
// ─────────────────────────────────────────────────────────────────────────────
export function parseProfileType(raw: unknown): ProfileType | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Customer': return ProfileType.Customer;
            case 'SalonOwner': return ProfileType.SalonOwner;
            case 'SalonStaff': return ProfileType.SalonStaff;
            case 'SuperAdmin': return ProfileType.SuperAdmin;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (
            raw === ProfileType.Customer ||
            raw === ProfileType.SalonOwner ||
            raw === ProfileType.SalonStaff ||
            raw === ProfileType.SuperAdmin
        ) {
            return raw;
        }
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// SalonStaffRole (no FE enum yet — pure-string union output)
// ─────────────────────────────────────────────────────────────────────────────
export type SalonStaffRoleInt = 1 | 2;   // StaffManager=1, Staff=2

export function parseSalonStaffRole(raw: unknown): SalonStaffRoleInt | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'StaffManager': return 1;
            case 'Staff': return 2;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw === 1 || raw === 2) return raw;
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// ApprovalStatus (uses the CategoryApprovalStatus enum already declared
// in api-responses.ts — the BE wire-form is identical across all
// ApprovalStatus consumers).
// ─────────────────────────────────────────────────────────────────────────────
export function parseApprovalStatus(raw: unknown): CategoryApprovalStatus | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Pending': return CategoryApprovalStatus.Pending;
            case 'Approved': return CategoryApprovalStatus.Approved;
            case 'Rejected': return CategoryApprovalStatus.Rejected;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (
            raw === CategoryApprovalStatus.Pending ||
            raw === CategoryApprovalStatus.Approved ||
            raw === CategoryApprovalStatus.Rejected
        ) {
            return raw;
        }
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// SubscriptionStatus (no FE enum yet — runtime int output to match the BE
// ordinals declared in `Devanza.Core.Enums.SubscriptionStatus`).
// ─────────────────────────────────────────────────────────────────────────────
export type SubscriptionStatusInt = 1 | 2 | 3 | 4 | 5 | 6;

export function parseSubscriptionStatus(raw: unknown): SubscriptionStatusInt | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Active': return 1;
            case 'Expired': return 2;
            case 'Cancelled': return 3;
            case 'PastDue': return 4;
            case 'Trial': return 5;
            case 'Incomplete': return 6;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw >= 1 && raw <= 6) return raw as SubscriptionStatusInt;
        return null;
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// AppointmentStatusKey (Calendar Settings table) — separate parser because
// the locked-status set and the BE→FE name mapping use the
// AppointmentStatusKey identity (Booked=1 maps to BE name "Pending", etc.).
// ─────────────────────────────────────────────────────────────────────────────
export function parseAppointmentStatusKey(raw: unknown): AppointmentStatusKey | null {
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Pending':   return AppointmentStatusKey.Booked;     // BE "Pending" = FE "Booked"
            case 'Booked':    return AppointmentStatusKey.Booked;
            case 'Approved':  return AppointmentStatusKey.Approved;
            case 'Completed': return AppointmentStatusKey.Completed;
            case 'Cancelled': return AppointmentStatusKey.Cancelled;
            case 'NoShow':    return AppointmentStatusKey.NoShow;
            case 'Arrived':   return AppointmentStatusKey.Arrived;
            case 'Started':   return AppointmentStatusKey.Started;
            default: return null;
        }
    }
    if (typeof raw === 'number') {
        if (raw >= 1 && raw <= 7) return raw as AppointmentStatusKey;
        return null;
    }
    return null;
}
